import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailSystem,
  InsertHrailSystemBody,
  UpdateHrailSystemNewImageBody,
  UpdateHrailSystemRemoveImageBody,
} from "./types/systems";

export const useHrailSystemsApi = () => {
  const api = useApi();

  const getAdminHrailSystem = useCallback(
    (systemId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/systems/${systemId}`;
      return useSWRMutation<HrailSystem, CustomError, string>(url, (url) => api.get<HrailSystem>({ url, signal }));
    },
    [api],
  );

  const useAdminHrailSystems = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/systems`, query);
      return useSWRImmutable<Paginated<HrailSystem>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailSystem>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailSystems = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/systems`, query);
      return api.get<Paginated<HrailSystem>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailSystem = useCallback(
    async (body: InsertHrailSystemBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailSystemBody>(`/admin/settings/hrail/systems`, body);
      mutateCache([`/admin/settings/hrail/systems`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailSystem = useCallback(
    async (systemId: string, body: UpdateHrailSystemRemoveImageBody | UpdateHrailSystemNewImageBody): Promise<void> => {
      const url = `/admin/settings/hrail/systems/${systemId}`;
      await api.put<UpdateHrailSystemRemoveImageBody | UpdateHrailSystemNewImageBody>(url, body);
      mutateCache([`/admin/settings/hrail/systems`], url);
    },
    [api],
  );

  const updateAdminHrailSystemVisibility = useCallback(
    async (systemId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/systems/${systemId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/systems`]);
    },
    [api],
  );

  const updateAdminHrailSystemOrder = useCallback(
    async (systemId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/systems/${systemId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/systems`]);
    },
    [api],
  );

  const deleteAdminHrailSystem = useCallback(
    async (systemId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/systems/${systemId}`);
      mutateCache([`/admin/settings/hrail/systems`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (systemId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/hrail/systems/${systemId}/image/generate_upload_url?file_name=${encodedFileName}`;
      const uploadUrl = await api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
      mutateCache([`/admin/settings/hrail/systems`]);
      return uploadUrl;
    },
    [api],
  );

  return {
    getAdminHrailSystem,
    useAdminHrailSystems,
    getAdminHrailSystems,
    insertAdminHrailSystem,
    updateAdminHrailSystem,
    updateAdminHrailSystemVisibility,
    updateAdminHrailSystemOrder,
    deleteAdminHrailSystem,
    generateUploadImageUrl,
  };
};
